/**
 * API Utility with Global Error Handling
 *
 * Provides fetch wrapper that:
 * - Shows toast notifications for errors
 * - Logs errors to backend for owner review
 * - Auto-retries on network failures
 * - Includes auth headers automatically
 */

import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Global token getter - set this once at app startup
let globalTokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the global auth token getter.
 * Call this once at app startup with the Clerk getToken function.
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  globalTokenGetter = getter;
  console.log('Auth token getter registered');
}

/**
 * Get the current auth token.
 * Races the Clerk getter against a 10-second timeout so a hung token refresh
 * does not leave every `finally { setLoading(false) }` waiting forever.
 */
export async function getAuthToken(): Promise<string | null> {
  if (globalTokenGetter) {
    try {
      const clerkToken = await Promise.race([
        globalTokenGetter(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Clerk token timeout')), 10000)
        ),
      ]);
      if (clerkToken) return clerkToken;
    } catch (e) {
      console.warn('Failed to get auth token:', e);
    }
  }
  // Fallback for the JWT portals that don't use Clerk (external witness
  // accountants, attorneys). They persist their bearer token in localStorage;
  // the generic api client must forward it so their pages can reach the shared
  // endpoints (e.g. the Accountant Export Center's workbook / reconciliation /
  // audit downloads, which are dual-auth on the backend). Clerk staff always
  // return above, so this never overrides a live Clerk session.
  try {
    return (
      localStorage.getItem('accountant_token') ||
      localStorage.getItem('attorney_token') ||
      null
    );
  } catch {
    return null;
  }
}

interface ApiOptions extends RequestInit {
  showErrorToast?: boolean;
  retries?: number;
  logError?: boolean;
  skipAuth?: boolean; // Skip auto-auth for this request
  timeoutMs?: number; // Opt-in per-request timeout (no global default — LLM/PDF calls are long)
}

interface ApiError {
  status: number;
  message: string;
  endpoint: string;
  timestamp: string;
  details?: any;
}

/**
 * Log error to backend for owner/admin review
 */
async function logErrorToBackend(error: ApiError, token?: string): Promise<void> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${BACKEND_URL}/api/v1/errors/log`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...error,
        user_agent: navigator.userAgent,
        url: window.location.href,
      }),
    });
  } catch (e) {
    // Silent fail - don't show error for error logging
    console.error('Failed to log error:', e);
  }
}

/**
 * Get user-friendly error message from API response
 */
function getErrorMessage(status: number, data: any): string {
  // Check for custom error message from backend
  if (data?.detail) {
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  if (data?.message) {
    return data.message;
  }
  if (data?.error) {
    return data.error;
  }

  // Default messages by status code
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Session expired. Please sign in again.';
    case 403:
      return 'You don\'t have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'The provided data is invalid.';
    case 429:
      return 'Too many requests. Please wait a moment.';
    case 500:
      return 'Server error. Our team has been notified.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again.';
    default:
      return `Request failed (Error ${status})`;
  }
}

/**
 * Enhanced fetch wrapper with error handling
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const {
    showErrorToast = true,
    retries = 0,
    logError = true,
    skipAuth = false,
    timeoutMs,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
  let lastError: Error | null = null;

  // Capture the caller's signal before the loop so retries don't chain onto
  // a stale controller from attempt 1.
  const callerSignal = fetchOptions.signal;

  // Auto-inject auth token if available and not skipped
  if (!skipAuth) {
    const token = await getAuthToken();
    if (token) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        'Authorization': `Bearer ${token}`
      };
    }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      // Opt-in per-request timeout (merged with any caller signal)
      if (timeoutMs && timeoutMs > 0) {
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        if (callerSignal) {
          // Merge caller signal with timeout signal
          callerSignal.addEventListener('abort', () => controller.abort());
        }
        fetchOptions.signal = controller.signal;
      } else if (callerSignal) {
        // No timeout — forward the caller's signal so each attempt is independently cancellable.
        fetchOptions.signal = callerSignal;
      }

      const response = await fetch(url, fetchOptions);

      if (timeoutId) clearTimeout(timeoutId);

      // Handle non-OK responses
      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch {
          // Response might not be JSON
        }

        const errorMessage = getErrorMessage(response.status, errorData);
        
        // Log error for owner review
        if (logError && response.status >= 500) {
          const apiError: ApiError = {
            status: response.status,
            message: errorMessage,
            endpoint: endpoint,
            timestamp: new Date().toISOString(),
            details: errorData,
          };
          
          // Get token from options if available
          const authHeader = fetchOptions.headers as Record<string, string>;
          const token = authHeader?.['Authorization']?.replace('Bearer ', '');
          logErrorToBackend(apiError, token);
        }

        // Show toast for user
        if (showErrorToast) {
          if (response.status >= 500) {
            toast.error('Something went wrong', {
              description: errorMessage,
            });
          } else if (response.status === 401) {
            toast.error('Authentication required', {
              description: errorMessage,
            });
          } else if (response.status === 403) {
            toast.error('Access denied', {
              description: errorMessage,
            });
          } else {
            toast.error('Request failed', {
              description: errorMessage,
            });
          }
        }

        const apiThrowError: any = new Error(errorMessage);
        apiThrowError.status = response.status;
        // Attach the parsed body so callers can match on a structured error code
        // (e.g. FastAPI's {detail:{code,message}}); .message keeps the human string.
        apiThrowError.data = errorData;
        throw apiThrowError;
      }

      // Parse successful response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      
      // Return blob for file downloads
      if (contentType?.includes('application/pdf') || 
          contentType?.includes('application/octet-stream') ||
          contentType?.includes('application/vnd')) {
        return await response.blob() as unknown as T;
      }

      return await response.text() as unknown as T;

    } catch (error: any) {
      if (timeoutId) clearTimeout(timeoutId);
      lastError = error;

      // Network error - retry if attempts remaining
      if (error.name === 'TypeError' && attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }

      // Network error on final attempt
      if (error.name === 'TypeError') {
        if (showErrorToast) {
          toast.error('Network error', {
            description: 'Please check your internet connection.',
          });
        }
      }

      throw error;
    }
  }

  throw lastError || new Error('Request failed');
}

/**
 * Convenience methods
 */
export const api = {
  get: <T = any>(endpoint: string, options?: ApiOptions) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * DELETE. Accepts an optional body — RFC 7231 permits DELETE with a
   * request body, and Phase 06 engine-override inactivation uses it
   * (`OverrideInactivateRequest` carries the inactivation reason).
   * Backwards-compatible: callers passing only `endpoint` (or
   * `endpoint, options`) keep working since body is optional and the
   * Content-Type header is only set when a body is present.
   */
  delete: <T = any>(endpoint: string, body?: any, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'DELETE',
      ...(body !== undefined
        ? {
            headers: {
              'Content-Type': 'application/json',
              ...options?.headers,
            },
            body: JSON.stringify(body),
          }
        : {}),
    }),

  /**
   * Upload file with FormData
   */
  upload: <T = any>(endpoint: string, formData: FormData, options?: ApiOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      // Don't set Content-Type - browser will set it with boundary
    }),
};

/**
 * Show success toast
 */
export function showSuccess(message: string, description?: string) {
  toast.success(message, { description });
}

/**
 * Show error toast
 */
export function showError(message: string, description?: string) {
  toast.error(message, { description });
}

/**
 * Show warning toast
 */
export function showWarning(message: string, description?: string) {
  toast.warning(message, { description });
}

/**
 * Show info toast
 */
export function showInfo(message: string, description?: string) {
  toast.info(message, { description });
}

/**
 * Upload files with FormData (special case for file uploads)
 * This bypasses the JSON body conversion for FormData support
 */
export async function uploadFiles(
  endpoint: string,
  formData: FormData,
  options: { showErrorToast?: boolean } = {}
): Promise<any> {
  const { showErrorToast = true } = options;
  
  const fullUrl = `${BACKEND_URL}${endpoint}`;
  console.log(`📤 uploadFiles called`);
  console.log(`📤 BACKEND_URL: "${BACKEND_URL}"`);
  console.log(`📤 endpoint: "${endpoint}"`);
  console.log(`📤 Full URL: "${fullUrl}"`);
  
  // Debug formData contents
  const formEntries: string[] = [];
  formData.forEach((value, key) => {
    if (value instanceof File) {
      formEntries.push(`${key}: File(${value.name}, ${value.size} bytes)`);
    } else {
      formEntries.push(`${key}: ${String(value).substring(0, 100)}`);
    }
  });
  console.log(`📤 FormData contents:`, formEntries);
  
  try {
    // Auto-inject auth token (same as apiRequest)
    const headers: Record<string, string> = {};
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    console.log(`📤 Response status: ${response.status} ${response.statusText}`);
    console.log(`📤 Response headers:`, Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Response error body:`, errorText);
      let errorData = {};
      try { errorData = JSON.parse(errorText); } catch {}
      const message = getErrorMessage(response.status, errorData);
      console.error(`uploadFiles error: ${message}`);
      if (showErrorToast) {
        toast.error('Upload failed', { description: message });
      }
      throw new Error(message);
    }
    
    const responseText = await response.text();
    console.log(`📤 Response body:`, responseText.substring(0, 500));
    
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      console.error(`Failed to parse JSON:`, e);
      throw new Error('Invalid JSON response from server');
    }
    
    console.log(`✅ uploadFiles success:`, result);
    return result;
  } catch (error) {
    console.error(`uploadFiles exception:`, error);
    if (showErrorToast && error instanceof Error) {
      toast.error('Upload failed', { description: error.message });
    }
    throw error;
  }
}

export default api;
